import { marketLocale, resolveApiBase, SportsApiClient, text, toArray, type WidgetProps } from '@sports/core'
import { useFeed } from '../../use-feed'

export const BOXING_FIGHTS_ATTRIBUTES = [
  'division',
  'weights',
  'lang',
  'limit',
  'heading',
  'refresh',
  'api-base',
  'theme',
] as const

interface BoxingFightsProps extends WidgetProps {
  division?: string
  weights?: string
  lang?: string
  limit?: string
  heading?: string
  refresh?: string
  apiBase?: string
}

/** Men's major weight classes — the default broad card when no division is given. */
const DEFAULT_WEIGHTS = [
  'heavyweight',
  'cruiserweight',
  'lightheavyweight',
  'supermiddleweight',
  'middleweight',
  'welterweight',
  'lightweight',
  'featherweight',
]

export const boxingFightsStyles = `
  .fights { display: flex; flex-direction: column; }
  .daygroup { display: flex; flex-direction: column; }
  .dayhead {
    padding: 0.4rem 1rem; font-size: 0.72rem; font-weight: 600;
    text-transform: uppercase; letter-spacing: 0.04em;
    color: var(--sports-muted); background: var(--sports-muted-bg);
    border-top: 1px solid var(--sports-border);
  }
  .fight {
    display: grid; grid-template-columns: 3.25rem 1fr auto; align-items: center;
    gap: 0 0.85rem; padding: 0.5rem 1rem;
    border-top: 1px solid var(--sports-border); font-size: 0.875rem;
  }
  .fight:first-child { border-top: none; }
  .when {
    color: var(--sports-muted); font-size: 0.75rem; line-height: 1.15;
    text-align: center; white-space: nowrap;
  }
  .when .t { display: block; color: var(--sports-fg); font-weight: 600; }
  .boxers { display: grid; gap: 0.2rem; min-width: 0; }
  .boxer { display: flex; align-items: center; gap: 0.4rem; overflow: hidden; }
  .boxer.win { font-weight: 700; }
  .boxer.lose { color: var(--sports-muted); }
  .boxer .nm { overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
  .boxer .mark { color: var(--sports-accent); font-size: 0.8rem; flex: none; }
  .bout {
    grid-column: 2 / -1; margin-top: 0.2rem;
    color: var(--sports-muted); font-size: 0.7rem;
    text-transform: uppercase; letter-spacing: 0.03em;
    overflow: hidden; text-overflow: ellipsis; white-space: nowrap;
  }
  .result {
    text-align: right; white-space: nowrap; font-size: 0.72rem;
    color: var(--sports-muted);
  }
  .result.method { color: var(--sports-fg); font-weight: 600; }
  .result.cancelled { color: var(--sports-live); }
`

interface Fight {
  id: string
  dateRaw: string
  dayKey: string
  dayLabel: string
  sortKey: number
  time: string
  status: string
  bout: string
  home: string
  away: string
  homeWin: boolean
  awayWin: boolean
  method: string
}

function number(value: string | undefined, fallback: number): number {
  const parsed = Number(value)
  return Number.isFinite(parsed) ? parsed : fallback
}

/** GoalServe dates arrive as DD.MM.YYYY; return the parts and a sortable stamp. */
function parseDate(raw: string): { date: Date | null; stamp: number } {
  const match = /^(\d{2})\.(\d{2})\.(\d{4})$/.exec(raw.trim())
  if (!match) return { date: null, stamp: 0 }
  const [, dd, mm, yyyy] = match
  const date = new Date(Number(yyyy), Number(mm) - 1, Number(dd))
  return { date, stamp: Number(`${yyyy}${mm}${dd}`) }
}

function timeStamp(time: string): number {
  const match = /^(\d{1,2}):(\d{2})$/.exec(time.trim())
  if (!match) return 0
  return Number(match[1]) * 60 + Number(match[2])
}

function dayLabel(date: Date | null, raw: string, lang: string): string {
  if (!date) return raw
  try {
    return date.toLocaleDateString(marketLocale(lang), {
      weekday: 'short',
      day: 'numeric',
      month: 'short',
      year: 'numeric',
    })
  } catch {
    return raw
  }
}

/** Method line for a finished bout, e.g. "KO R3" / "Points R12". */
function methodOf(reason: string, round: string): string {
  const r = reason.trim()
  if (!r) return ''
  const label = r.length <= 3 ? r.toUpperCase() : r.charAt(0).toUpperCase() + r.slice(1)
  return round.trim() ? `${label} R${round.trim()}` : label
}

function parseFights(payloads: unknown[], lang: string): Fight[] {
  const out: Fight[] = []
  const seen = new Set<string>()
  for (const payload of payloads) {
    const scores = (payload as { scores?: { category?: unknown } })?.scores
    for (const category of toArray<Record<string, unknown>>(
      scores?.category as Record<string, unknown> | Record<string, unknown>[] | undefined,
    )) {
      const bout = text(category, 'name')
      for (const match of toArray<Record<string, unknown>>(
        category.match as Record<string, unknown> | Record<string, unknown>[] | undefined,
      )) {
        const id = text(match, 'id')
        if (id && seen.has(id)) continue
        if (id) seen.add(id)
        const home = (match.localteam ?? match.hometeam) as Record<string, unknown> | undefined
        const away = (match.awayteam ?? match.visitorteam) as Record<string, unknown> | undefined
        const dateRaw = text(match, 'date')
        const { date, stamp } = parseDate(dateRaw)
        const time = text(match, 'time')
        const homeWin = text(home, 'winner') === 'True'
        const awayWin = text(away, 'winner') === 'True'
        const winner = homeWin ? home : awayWin ? away : undefined
        out.push({
          id: id || `${out.length}`,
          dateRaw,
          dayKey: dateRaw,
          dayLabel: dayLabel(date, dateRaw, lang),
          sortKey: stamp * 10000 + timeStamp(time),
          time,
          status: text(match, 'status'),
          bout,
          home: text(home, 'name'),
          away: text(away, 'name'),
          homeWin,
          awayWin,
          method: methodOf(text(winner, 'reason'), text(winner, 'round')),
        })
      }
    }
  }
  out.sort((a, b) => a.sortKey - b.sortKey)
  return out
}

interface DayGroup {
  key: string
  label: string
  fights: Fight[]
}

function groupByDay(fights: Fight[]): DayGroup[] {
  const groups: DayGroup[] = []
  const index = new Map<string, DayGroup>()
  for (const fight of fights) {
    let group = index.get(fight.dayKey)
    if (!group) {
      group = { key: fight.dayKey, label: fight.dayLabel, fights: [] }
      index.set(fight.dayKey, group)
      groups.push(group)
    }
    group.fights.push(fight)
  }
  return groups
}

/**
 * Boxing fight card — upcoming bouts grouped by date, SofaScore-style: the two
 * fighters, the weight class / belt on the line, and the date/time; a finished
 * bout bolds the winner and shows the method.
 *
 * The data comes from GoalServe's per-weight-class schedule feeds
 * (`<class>_shedule`). `division` shows a single class; `weights` (comma list)
 * merges several. With neither, the men's major classes are fetched and merged.
 */
export function BoxingFights({
  division,
  weights,
  lang,
  limit,
  heading,
  refresh,
  apiBase,
}: BoxingFightsProps) {
  const max = number(limit, 24)
  const refreshSeconds = number(refresh, 300)
  const base = resolveApiBase(apiBase)

  const list = (division || weights || DEFAULT_WEIGHTS.join(','))
    .split(',')
    .map((w) => w.trim().toLowerCase())
    .filter(Boolean)
  // A schedule feed is the weight class suffixed `_shedule` (upstream spelling).
  const paths = Array.from(
    new Set(list.map((w) => (w.endsWith('_shedule') ? w : `${w}_shedule`))),
  )
  const key = paths.join(',')

  const { data, error, loading } = useFeed(
    (signal) => {
      const client = new SportsApiClient(base)
      // Tolerate a single class 503-ing so it never blanks the whole card.
      return Promise.all(paths.map((p) => client.feed('boxing', p, signal).catch(() => null)))
    },
    [base, key],
    refreshSeconds,
  )

  const fights = data ? parseFights(data, lang ?? '').slice(0, max) : []
  const groups = groupByDay(fights)

  return (
    <div class="sports-card">
      <div class="sports-head">
        <span>{heading ?? 'Boxing'}</span>
      </div>

      {loading && <div class="sports-note">Loading…</div>}
      {!loading && error && (
        <div class="sports-note error">Fights are unavailable right now.</div>
      )}
      {!loading && !error && fights.length === 0 && (
        <div class="sports-note">No fights scheduled.</div>
      )}

      <div class="fights">
        {groups.map((group) => (
          <div class="daygroup" key={group.key}>
            <div class="dayhead">{group.label}</div>
            {group.fights.map((f) => {
              const finished = f.homeWin || f.awayWin
              const cancelled = /cancel/i.test(f.status)
              return (
                <div class="fight" key={f.id}>
                  <span class="when">
                    {finished ? <span class="t">FT</span> : f.time && <span class="t">{f.time}</span>}
                  </span>
                  <span class="boxers">
                    <span
                      class={
                        f.homeWin ? 'boxer win' : f.awayWin ? 'boxer lose' : 'boxer'
                      }
                    >
                      <span class="nm">{f.home}</span>
                      {f.homeWin && <span class="mark">✓</span>}
                    </span>
                    <span
                      class={
                        f.awayWin ? 'boxer win' : f.homeWin ? 'boxer lose' : 'boxer'
                      }
                    >
                      <span class="nm">{f.away}</span>
                      {f.awayWin && <span class="mark">✓</span>}
                    </span>
                  </span>
                  {cancelled ? (
                    <span class="result cancelled">Cancelled</span>
                  ) : finished && f.method ? (
                    <span class="result method">{f.method}</span>
                  ) : finished ? (
                    <span class="result">Finished</span>
                  ) : (
                    <span class="result" />
                  )}
                  {f.bout && <span class="bout">{f.bout}</span>}
                </div>
              )
            })}
          </div>
        ))}
      </div>
    </div>
  )
}
